import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Sparkles, Copy, Check, Loader2, AlertTriangle, RefreshCcw,
  CheckCircle2, TrendingUp, TrendingDown, Minus,
} from "lucide-react";

/**
 * AI-powered inline-fix drawer for SimpleDashboard action cards.
 *
 * Three phases:
 *   1. generating  — Claude is writing the suggestion
 *   2. ready       — user can copy + regenerate, or click "I applied it"
 *   3. verifying   — re-running the audit to confirm the fix landed
 *   4. verified    — show old → new score delta + celebrate
 *
 * Props:
 *   projectId      – project id used to call POST /projects/:id/generate-fix
 *   action         – the action object from next-actions
 *   currentScore   – the score the SimpleDashboard is currently showing (used as
 *                    the baseline for the delta)
 *   open           – controlled boolean
 *   onOpenChange(bool)
 *   onFixApplied() – called after a verified re-audit so the parent can
 *                    refresh actions/score
 */
export default function InlineFixDrawer({
  projectId,
  action,
  currentScore,
  open,
  onOpenChange,
  onFixApplied,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fix, setFix] = useState(null);
  const [copied, setCopied] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null); // {oldScore, newScore, delta}
  const [verifyError, setVerifyError] = useState("");

  const fetchFix = async () => {
    if (!projectId || !action?.raw) return;
    setLoading(true);
    setError("");
    setFix(null);
    setCopied(false);
    setVerifyResult(null);
    setVerifyError("");
    try {
      const { data } = await api.post(`/projects/${projectId}/generate-fix`, {
        raw_issue: action.raw,
      });
      setFix(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate when drawer opens for a new action.
  useEffect(() => {
    if (open) fetchFix();
    if (!open) {
      setFix(null);
      setError("");
      setLoading(false);
      setCopied(false);
      setVerifying(false);
      setVerifyResult(null);
      setVerifyError("");
    }
  }, [open, action?.raw]);

  const copy = async () => {
    if (!fix?.suggestion) return;
    try {
      await navigator.clipboard.writeText(fix.suggestion);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — select and copy manually");
    }
  };

  const verify = async () => {
    if (!projectId) return;
    setVerifying(true);
    setVerifyError("");
    try {
      const { data: audit } = await api.post(`/projects/${projectId}/audit`);
      const newScore = typeof audit?.score === "number" ? audit.score : null;
      const oldScore = typeof currentScore === "number" ? currentScore : null;
      const delta = oldScore !== null && newScore !== null ? newScore - oldScore : null;
      setVerifyResult({ oldScore, newScore, delta });
      if (delta != null && delta > 0) {
        toast.success(`Score went up by ${delta} pts — ${oldScore} → ${newScore}`);
      } else if (delta === 0) {
        toast.message("Score didn't move", {
          description: "Make sure the fix is live on the public page, then try again.",
        });
      } else if (delta != null && delta < 0) {
        toast.message(`Score dropped ${Math.abs(delta)} pts`, {
          description: "Something else may have changed on the page.",
        });
      }
    } catch (e) {
      setVerifyError(formatApiError(e));
    } finally {
      setVerifying(false);
    }
  };

  const closeAndRefresh = () => {
    if (typeof onFixApplied === "function") onFixApplied();
    onOpenChange(false);
  };

  const charCount = fix?.suggestion ? fix.suggestion.length : 0;
  const deltaIsWin = verifyResult && verifyResult.delta != null && verifyResult.delta > 0;
  const deltaIsFlat = verifyResult && verifyResult.delta === 0;
  const deltaIsLoss = verifyResult && verifyResult.delta != null && verifyResult.delta < 0;
  const DeltaIcon = deltaIsWin ? TrendingUp : deltaIsLoss ? TrendingDown : Minus;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="rounded-none border-foreground bg-background" data-testid="inline-fix-drawer">
        <div className="mx-auto w-full max-w-2xl px-6 pb-6">
          <DrawerHeader className="px-0">
            <div className="flex items-center gap-2 font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
              <Sparkles className="w-3 h-3" /> AI fix · powered by Arlo
            </div>
            <DrawerTitle className="font-display tracking-tight text-2xl">
              {action?.title || "Generate inline fix"}
            </DrawerTitle>
            {action?.description && (
              <DrawerDescription className="text-sm text-muted-foreground">
                {action.description}
              </DrawerDescription>
            )}
          </DrawerHeader>

          {/* Body */}
          <div className="mt-4">
            {/* PHASE 1: generating */}
            {loading && (
              <div className="border border-dashed border-foreground/30 p-8 flex items-center gap-3 text-sm text-muted-foreground" data-testid="inline-fix-loading">
                <Loader2 className="w-4 h-4 animate-spin" />
                Arlo is writing a fix tailored to your page…
              </div>
            )}

            {/* Generate error */}
            {error && !loading && (
              <div className="border border-red-300 bg-red-50 text-red-800 p-4 flex items-start gap-3" data-testid="inline-fix-error">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold tracking-tight">Couldn&apos;t generate a fix</div>
                  <div className="mt-1 opacity-80">{error}</div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3 rounded-none"
                    onClick={fetchFix}
                    data-testid="inline-fix-retry"
                  >
                    <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Try again
                  </Button>
                </div>
              </div>
            )}

            {/* PHASE 2: ready (suggestion shown, copy + apply CTAs) */}
            {fix && !loading && !verifyResult && (
              <div className="space-y-3" data-testid="inline-fix-result">
                <div className="flex items-center justify-between gap-2 flex-wrap font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                  <span>// {fix.heading}</span>
                  <span>
                    {charCount} chars
                    {fix.char_target && (
                      <span className="ml-2 opacity-70">· target {fix.char_target}</span>
                    )}
                  </span>
                </div>
                <div
                  className="border border-foreground bg-white p-4 whitespace-pre-wrap text-sm font-mono-data leading-relaxed select-text"
                  data-testid="inline-fix-suggestion"
                >
                  {fix.suggestion}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    onClick={copy}
                    className="rounded-none bg-foreground hover:bg-foreground/90"
                    data-testid="inline-fix-copy"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-2" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" /> Copy to clipboard
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={fetchFix}
                    className="rounded-none"
                    data-testid="inline-fix-regenerate"
                  >
                    <RefreshCcw className="w-4 h-4 mr-2" /> Regenerate
                  </Button>
                </div>

                {/* Closure-loop hand-off */}
                <div className={`mt-2 border ${copied ? "border-emerald-500 bg-emerald-50" : "border-dashed border-foreground/30 bg-secondary/40"} p-4`} data-testid="inline-fix-apply-block">
                  <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                    // step 2 · apply it
                  </div>
                  <div className="text-sm mt-1.5 leading-relaxed">
                    Paste this into your CMS (WordPress SEO plugin, Shopify metafields, Webflow SEO
                    settings, etc.) and publish. Then click below — Arlo will re-audit your page and show
                    the score climb live.
                  </div>
                  <Button
                    onClick={verify}
                    disabled={verifying}
                    className={`mt-3 rounded-none ${copied ? "bg-emerald-600 hover:bg-emerald-700" : "bg-foreground hover:bg-foreground/90"}`}
                    data-testid="inline-fix-verify"
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Re-auditing your page…
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" /> I applied it — re-check my score
                      </>
                    )}
                  </Button>
                  {verifyError && (
                    <div className="mt-2 text-xs text-rose-700" data-testid="inline-fix-verify-error">
                      {verifyError}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* PHASE 4: verified — show delta */}
            {verifyResult && (
              <div
                className={`border-2 p-6 ${
                  deltaIsWin
                    ? "border-emerald-500 bg-emerald-50"
                    : deltaIsFlat
                      ? "border-amber-400 bg-amber-50"
                      : "border-rose-400 bg-rose-50"
                }`}
                data-testid="inline-fix-verified"
              >
                <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                  // re-audit complete
                </div>
                <div className="mt-3 flex items-end gap-4">
                  <div>
                    <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">before</div>
                    <div className="font-semibold tracking-tight text-4xl leading-none">
                      {verifyResult.oldScore ?? "—"}
                    </div>
                  </div>
                  <div className="text-3xl text-muted-foreground pb-0.5">→</div>
                  <div>
                    <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">after</div>
                    <div className={`font-semibold tracking-tight text-5xl leading-none ${
                      deltaIsWin ? "text-emerald-700" : deltaIsLoss ? "text-rose-700" : "text-amber-700"
                    }`}>
                      {verifyResult.newScore ?? "—"}
                    </div>
                  </div>
                  {verifyResult.delta != null && (
                    <div className={`pb-1 inline-flex items-center gap-1 font-semibold ${
                      deltaIsWin ? "text-emerald-700" : deltaIsLoss ? "text-rose-700" : "text-amber-700"
                    }`} data-testid="inline-fix-delta">
                      <DeltaIcon className="w-5 h-5" strokeWidth={2.5} />
                      {verifyResult.delta > 0 ? `+${verifyResult.delta}` : verifyResult.delta} pts
                    </div>
                  )}
                </div>
                <div className="mt-4 text-sm leading-relaxed">
                  {deltaIsWin && (
                    <>Nice work — that fix is live. Your projected score climbed by{" "}
                      <span className="font-semibold">{verifyResult.delta} pts</span>. Tackle the next
                      action when you&apos;re ready.
                    </>
                  )}
                  {deltaIsFlat && (
                    <>Your score didn&apos;t change. The audit didn&apos;t see the fix on the page yet — double-check
                      it&apos;s saved + published, and that the URL Arlo audits matches the page you edited.
                    </>
                  )}
                  {deltaIsLoss && (
                    <>Your score dropped {Math.abs(verifyResult.delta)} pts. Something else may have changed
                      on the page during your edit. Open the full project to see the latest issues.
                    </>
                  )}
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    onClick={closeAndRefresh}
                    className="rounded-none bg-foreground hover:bg-foreground/90"
                    data-testid="inline-fix-see-updated"
                  >
                    See updated actions
                  </Button>
                  {!deltaIsWin && (
                    <Button
                      variant="outline"
                      onClick={verify}
                      disabled={verifying}
                      className="rounded-none"
                      data-testid="inline-fix-recheck"
                    >
                      {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
                      Re-check again
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          <DrawerFooter className="px-0 pt-6">
            <DrawerClose asChild>
              <Button variant="outline" className="rounded-none" data-testid="inline-fix-close">
                Close
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
